// A folder or zip of audio from any DAW → a seed.
//
// One decision per step: drop → what are these (a seed, or clips for the
// library)? → a seed: is it a remix? (and of what) → name it and pick tracks
// → watch it go up. Clips go to the library's form instead and never meet the
// remix question. The flow (UploadFlow) is the only upload there is: this
// page frames it, and the home modal's Plant and the library page's Upload
// modal embed it. The page holds the files in memory the
// whole way, above any ["web", …] query gate, so signing in at Confirm (the
// auth modal, not a navigation) never loses them. The work is in
// lib/web-upload/ and hooks/useStemsUpload.ts; this is presentation.
//
// Loose loops and one-shots go to the producer's private clip library instead
// ("Mine" in the room's Material sidebar), through components/ClipsForm.tsx.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CheckCircle, ChevronLeft, Layers, Loader2, Play, RotateCcw, Shapes, Shuffle, Square, X, XCircle } from "lucide-react";

import type { ApiEventView, FeedCard, FeedPage, SearchResult, SeedApiDetail } from "@shared/api-types.js";
import { Button } from "@web-shared/components/ui/button";
import { track } from "@web-shared/lib/analytics";
import { cn } from "@web-shared/lib/utils";
import { drawEnvelope, prepareCanvas } from "@web-shared/lib/waveform";
import { json, webApi } from "@web-shared/lib/web-api";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { eventPath, localRoomPath, seedPath } from "@/lib/ctas";
import { classify, type UploadKind } from "@/lib/web-upload/classify";
import { intakeFromDataTransfer, intakeFromFileList, IntakeError, type Intake } from "@/lib/web-upload/intake";
import { limitProblem } from "@/lib/web-upload/limits";
import { bpmFromNames, titleFromFiles, titleFromName, trackNames } from "@/lib/web-upload/naming";
import { suggestBpm, type BpmSuggestion } from "@/lib/web-upload/tempo";
import { listenTo, type Heard } from "@/lib/web-upload/listen";
import { readFiles, type ReadFile } from "@/lib/web-upload/read-files";
import { overallProgress, prepareFraction, secondsLeft, timeLeftText, type ProgressStage } from "@/lib/web-upload/progress";
import { useAuthGate } from "../auth";
import { useSession } from "../session";
import { UploadDropZone } from "../components/UploadDropZone";
import { ClipsForm } from "../components/ClipsForm";
import { canRetry, useStemsUpload, type JobView } from "../hooks/useStemsUpload";
import { useUnsavedGuard } from "../hooks/useUnsavedGuard";
import { useStemPreview } from "../hooks/useStemPreview";
import { useStartJam } from "../hooks/useStartJam";
import { AmbientGlow } from "@/components/AmbientGlow";
import type { EventReturnState } from "./EventPage";

/**
 * Uploading for an event's pool (`?event=slug`): a seed only — never the
 * clip library — named for the event, pooled the moment it's live, and back
 * to the event page (resuming Join at `then` when it was mid-Join).
 */
interface ForEvent {
  slug: string;
  title: string | null;
  then: "discord" | null;
}

interface Row {
  include: boolean;
  name: string;
}

/** What a seed is, in one line, for whoever is about to plant their first. */
const SEED_EXPLAINER = "A seed is your idea, shared as stems — anyone can remix it in Ableton or the browser, live or solo.";

type Step = "drop" | "remix" | "parent" | "confirm" | "form" | "upload" | "library";

export function UploadFilesPage() {
  useDocumentTitle("Plant a seed");
  const [params] = useSearchParams();
  const parentId = params.get("parent");
  const eventSlug = parentId === null ? params.get("event") : null;
  return (
    <>
      <AmbientGlow />
      <div className="relative z-10 mx-auto max-w-xl space-y-5">
        <UploadFlow
          initialParentId={parentId}
          eventSlug={eventSlug}
          eventThen={params.get("then") === "discord" ? "discord" : null}
        />
      </div>
    </>
  );
}

/**
 * The whole upload, from the drop to the seed. `initialParentId` skips the
 * remix question (the person came from that seed); `eventSlug` uploads for an
 * event's pool. `embedded`: inside a modal, which brings its own way back and
 * its own frame. `thenJam`: Plant it & Start Jam — the jam opens on the new
 * seed the moment it's live. `initialIntake`: files already dropped (on the
 * library page), read at once. `onClose`: the host's way to close; clips are
 * handed to the host's own upload status when it has one.
 */
export function UploadFlow({
  initialParentId,
  eventSlug = null,
  eventThen = null,
  embedded = false,
  thenJam = false,
  initialIntake = null,
  onClose,
  onWorking,
  dropHeading = null,
}: {
  initialParentId: string | null;
  eventSlug?: string | null;
  eventThen?: "discord" | null;
  embedded?: boolean;
  thenJam?: boolean;
  initialIntake?: Promise<Intake> | null;
  onClose?: () => void;
  /** A seed is going up: closing the host now would stop it. */
  onWorking?: (working: boolean) => void;
  /** Embedded, the drop step's heading when the host has no title of its own. */
  dropHeading?: { title: string; sub?: string } | null;
}) {
  const [parentId, setParentId] = useState<string | null>(initialParentId);
  const session = useSession();
  const eventQuery = useQuery({
    queryKey: ["web", "event", eventSlug ?? "", session?.user.id ?? ""],
    enabled: eventSlug !== null,
    queryFn: () => webApi<ApiEventView>(`/events/${encodeURIComponent(eventSlug!)}`),
  });
  const forEvent: ForEvent | null = eventSlug
    ? { slug: eventSlug, title: eventQuery.data?.event.title ?? null, then: eventThen }
    : null;
  const parent = useQuery({
    queryKey: ["web", "seed", parentId ?? "", session?.user.id ?? ""],
    enabled: parentId !== null,
    queryFn: () => webApi<SeedApiDetail>(`/seeds/${encodeURIComponent(parentId!)}`),
  });

  const [step, setStep] = useState<Step>("drop");
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [intake, setIntake] = useState<Intake | null>(null);
  const [files, setFiles] = useState<ReadFile[]>([]);
  const [unreadable, setUnreadable] = useState<string[]>([]);
  const [guess, setGuess] = useState<UploadKind>("stems");
  const [chosen, setChosen] = useState<UploadKind>("stems");
  const [suggested, setSuggested] = useState<BpmSuggestion>({ bpm: null, source: "typed" });
  const [waves, setWaves] = useState<Array<Float32Array | null>>([]);
  const [heard, setHeard] = useState<Heard | null>(null);

  const reset = () => {
    setStep("drop");
    setParentId(initialParentId);
    setProblem(null);
    setIntake(null);
    setFiles([]);
    setUnreadable([]);
  };

  const read = async (take: () => Promise<Intake>) => {
    setProblem(null);
    setBusy("Opening…");
    try {
      const got = await take();
      track("web_upload_started", { value: got.files.length, detail: `skipped=${got.skipped}` });
      if (got.files.length === 0) {
        track("web_upload_failed", { detail: "no_audio" });
        setProblem("No audio files in there. Seedbed takes WAV, AIFF, FLAC and MP3.");
        return;
      }
      const { ok, bad } = await readFiles(got.files, (i, n) => setBusy(`Reading ${i + 1} of ${n}…`));
      if (ok.length === 0) {
        track("web_upload_failed", { detail: "unreadable" });
        setProblem("None of those files could be read as audio. Are any of them empty?");
        return;
      }
      const kind = classify(ok.map((f) => f.probe.durationSec));
      // One listen: every file's waveform, and the tempo of the stems together.
      const heard = await listenTo(ok.map((f) => f.file), (i, n) => setBusy(`Listening ${i + 1} of ${n}…`), { features: true });
      const suggestion = suggestBpm(
        kind,
        { folder: bpmFromNames(got.rootName, []), any: bpmFromNames(got.rootName, ok.map((f) => f.file.path)) },
        heard.tempo,
      );
      setWaves(heard.waves);
      setHeard(heard);
      setSuggested(suggestion);
      setIntake(got);
      setFiles(ok);
      setUnreadable(bad);
      setGuess(kind);
      setChosen(kind);
      // For an event it's a seed whatever it looks like: nothing to ask.
      // Everything else says first what it is; a seed is then asked about
      // remixing (confirm, below).
      setStep(eventSlug ? "form" : "confirm");
    } catch (err) {
      track("web_upload_failed", { detail: "unreadable" });
      setProblem(err instanceof IntakeError ? err.message : "Those files couldn't be opened.");
    } finally {
      setBusy(null);
    }
  };

  /** `library`: the clips go to the producer's library, not into a seed.
   * A seed is asked whether it's a remix — unless it came from its parent. */
  const confirm = (kind: UploadKind, to: "seed" | "library" = "seed") => {
    track("web_upload_classified", { detail: `${kind === guess ? kind : `${kind}|override`}${to === "library" ? "|library" : ""}`, value: files.length });
    setChosen(kind);
    setStep(to === "library" ? "library" : initialParentId ? "form" : "remix");
  };

  // Files dropped before the flow opened (the library page): read them now.
  const startedWith = useRef<Promise<Intake> | null>(null);
  useEffect(() => {
    if (!initialIntake || startedWith.current === initialIntake) return;
    startedWith.current = initialIntake;
    void read(() => initialIntake);
  }, [initialIntake]);

  // One heading per step, naming what that step asks — the upload itself
  // speaks for its own progress and result.
  const seedTitle = parent.data
    ? `${session?.user.id === parent.data.producer.id ? "New version of" : "Remix"} "${parent.data.title}"`
    : "Plant a seed";
  const skipped = (intake?.skipped ?? 0) + unreadable.length;
  const heading: { title: string; sub?: string } | null =
    step === "drop"
      ? dropHeading ?? (forEvent
        ? { title: "Plant a seed for the pool", sub: "One project, as stems or a single bounce. Someone else at the event flips it." }
        : { title: seedTitle, sub: parentId ? undefined : SEED_EXPLAINER })
      : step === "remix"
        ? { title: "Is this a remix?" }
      : step === "parent"
        ? { title: "What are you remixing?" }
      : step === "confirm"
        ? {
            title: "What are these?",
            sub: [`${files.length} file${files.length === 1 ? "" : "s"}`, skipped > 0 ? `${skipped} skipped` : null].filter(Boolean).join(" · "),
          }
        : step === "library"
          ? { title: "Add to your library" }
          : step === "form"
            ? { title: parent.data ? seedTitle : "Name your seed" }
            : null;

  return (
    <div className="space-y-5">
      {!embedded && step !== "upload" && (
        <Link
          to={forEvent ? eventPath(forEvent.slug) : parentId ? seedPath(parentId) : "/"}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="size-3.5" /> {forEvent ? "back to the event" : parentId ? "back to the seed" : "back"}
        </Link>
      )}
      {forEvent && (
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-primary">
            <Shuffle className="size-3.5" /> For {forEvent.title ?? "Secret Sample"}
          </span>
        </div>
      )}
      {/* Embedded, the modal's own title already says what the drop is for. */}
      {heading && !(embedded && step === "drop" && !dropHeading) && (
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{heading.title}</h1>
          {heading.sub && <p className="mt-1 text-sm text-muted-foreground">{heading.sub}</p>}
        </div>
      )}

      {step === "drop" && (
        <>
          {/* Embedded, the heading (and its line on what a seed is) is the
              modal's: say it here, before the drop. Not for a remix (it has
              its seed), the event pool or the library's own heading. */}
          {embedded && !forEvent && !parentId && !dropHeading && (
            <p className="text-sm text-muted-foreground">{SEED_EXPLAINER}</p>
          )}
          <UploadDropZone
            busy={busy}
            onDrop={(dt) => void read(() => intakeFromDataTransfer(dt))}
            onPick={(list) => void read(() => intakeFromFileList(list))}
          />
          {problem && <Problem text={problem} />}
        </>
      )}

      {step === "remix" && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <ChoiceCard icon={<Shuffle className="size-5" />} title="Yes" result="Pick the seed" likely={false} onClick={() => setStep("parent")} />
            <ChoiceCard
              icon={<Layers className="size-5" />}
              title="No, it's an original"
              result="Carry on"
              likely={false}
              onClick={() => {
                setParentId(initialParentId);
                setStep("form");
              }}
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setParentId(initialParentId);
              setStep("confirm");
            }}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Back
          </button>
        </div>
      )}

      {step === "parent" && (
        <ParentPicker
          onPick={(id) => {
            setParentId(id);
            setStep("form");
          }}
          onBack={() => setStep("remix")}
        />
      )}

      {step === "confirm" && intake && (
        <Confirm
          guess={guess}
          onChoose={confirm}
          onCancel={reset}
          // The library's table takes no AAC yet (workers/src/library-clips.ts):
          // a phone's .m4a becomes a seed's tracks.
          canLibrary={parentId === null && !intake.files.some((f) => f.ext === "m4a" || f.ext === "aac")}
        />
      )}

      {step === "library" && <ClipsForm files={files} heard={heard} onBack={() => setStep("confirm")} onDone={reset} onHandOff={onClose} />}

      {/* One element for both steps: the form owns the upload, so it must not remount when it starts. */}
      {(step === "form" || step === "upload") && intake && (
        <SeedForm
          files={files}
          intake={intake}
          chosen={chosen}
          suggested={suggested}
          waves={waves}
          parent={parent.data ?? null}
          forEvent={forEvent}
          onBack={() => (forEvent ? reset() : setStep(initialParentId ? "confirm" : "remix"))}
          onStarted={() => setStep("upload")}
          onEdit={() => setStep("form")}
          onDone={reset}
          uploading={step === "upload"}
          thenJam={thenJam}
          onWorking={onWorking}
        />
      )}
    </div>
  );
}

/**
 * Which seed this is a remix of: today's hot seeds to start with, search for
 * anything else. One tap picks.
 */
function ParentPicker({ onPick, onBack }: { onPick: (seedId: string) => void; onBack: () => void }) {
  const [q, setQ] = useState("");
  const term = q.trim();
  const hot = useQuery({
    queryKey: ["web", "feed", "discover", "picker"],
    enabled: term === "",
    queryFn: () => webApi<FeedPage>("/feed?tab=discover"),
  });
  const found = useQuery({
    queryKey: ["web", "search", term],
    enabled: term !== "",
    queryFn: () => webApi<SearchResult>(`/search?q=${encodeURIComponent(term)}`),
  });
  const cards: FeedCard[] = (term === "" ? hot.data?.items : found.data?.seeds) ?? [];
  const loading = term === "" ? hot.isPending : found.isPending;
  return (
    <div className="space-y-3">
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search seeds"
        aria-label="Search seeds"
        autoFocus
        className="h-11 w-full rounded-xl border border-white/10 bg-[#0d0d12] px-4 text-sm placeholder:text-muted-foreground/60"
      />
      <div className="max-h-72 space-y-1.5 overflow-y-auto">
        {loading ? (
          <p className="flex items-center gap-2 py-4 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Looking…</p>
        ) : cards.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">No seed by that name. Try a producer, or a word from the title.</p>
        ) : (
          cards.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onPick(c.id)}
              className="flex w-full items-center gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2.5 text-left hover:border-primary/40 hover:bg-primary/5"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold">{c.title}</span>
                <span className="block truncate text-xs text-muted-foreground">{c.producer.username} · {c.bpm} BPM</span>
              </span>
              <ArrowRight className="size-4 shrink-0 text-muted-foreground" />
            </button>
          ))
        )}
      </div>
      <button type="button" onClick={onBack} className="text-xs text-muted-foreground hover:text-foreground">
        Back
      </button>
    </div>
  );
}

function Problem({ text }: { text: string }) {
  return (
    <p className="flex items-start gap-2 text-sm text-removed">
      <XCircle className="mt-0.5 size-4 shrink-0" /> {text}
    </p>
  );
}

/**
 * One question — what did you drop? — as two answers side by side. The guess
 * is marked, not chosen for you: a tap on either card goes on.
 */
function Confirm({
  guess,
  onChoose,
  onCancel,
  canLibrary,
}: {
  guess: UploadKind;
  onChoose: (kind: UploadKind, to?: "seed" | "library") => void;
  onCancel: () => void;
  /** A remix is a seed by definition; only a fresh upload can go to the library. */
  canLibrary: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <ChoiceCard
          icon={<Layers className="size-5" />}
          title="Full project / stems"
          result="Becomes a seed you plant"
          likely={guess === "stems"}
          onClick={() => onChoose("stems")}
        />
        <ChoiceCard
          icon={<Shapes className="size-5" />}
          title="Sample pack / loops"
          result={canLibrary ? "Goes to your private clip library" : "Become tracks in your version"}
          likely={guess === "clips"}
          onClick={() => (canLibrary ? onChoose("clips", "library") : onChoose("clips"))}
        />
      </div>
      <button type="button" onClick={onCancel} className="text-xs text-muted-foreground hover:text-foreground">
        Cancel
      </button>
    </div>
  );
}

function ChoiceCard({
  icon,
  title,
  result,
  likely,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  result: string;
  likely: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group relative flex flex-col items-start gap-3 rounded-2xl border bg-raised p-4 text-left transition-colors",
        likely ? "border-primary/60 ring-1 ring-primary/30 hover:bg-primary/5" : "border-hairline hover:border-white/20 hover:bg-white/[0.03]",
      )}
    >
      {likely && (
        <span className="absolute right-3 top-3 rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
          Our guess
        </span>
      )}
      <span className={cn("flex size-10 items-center justify-center rounded-xl", likely ? "bg-primary/15 text-primary" : "bg-white/5 text-muted-foreground")}>
        {icon}
      </span>
      <span>
        <span className="block text-sm font-semibold">{title}</span>
        <span className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground group-hover:text-foreground">
          {result} <ArrowRight className="size-3" />
        </span>
      </span>
    </button>
  );
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function SeedForm({
  files,
  intake,
  chosen,
  suggested,
  waves,
  parent,
  forEvent,
  onBack,
  onStarted,
  onEdit,
  onDone,
  uploading,
  thenJam = false,
  onWorking,
}: {
  files: ReadFile[];
  intake: Intake;
  chosen: UploadKind;
  suggested: BpmSuggestion;
  waves: Array<Float32Array | null>;
  parent: SeedApiDetail | null;
  forEvent: ForEvent | null;
  onBack: () => void;
  onStarted: () => void;
  /** Back from a failed upload to this form, files and fields kept. */
  onEdit: () => void;
  onDone: () => void;
  uploading: boolean;
  thenJam?: boolean;
  onWorking?: (working: boolean) => void;
}) {
  const gate = useAuthGate();
  const upload = useStemsUpload();
  const names = useMemo(() => trackNames(files.map((f) => f.file.name)), [files]);
  const [title, setTitle] = useState(
    () => titleFromName(intake.rootName) || titleFromFiles(files.map((f) => f.file.name)) || (parent?.title ?? ""),
  );
  // A remix with nothing better to go on starts from its parent's tempo.
  const start: BpmSuggestion =
    suggested.bpm === null && parent ? { bpm: parent.bpm, source: "parent" } : suggested;
  const [bpm, setBpm] = useState(start.bpm !== null ? String(start.bpm) : "");
  const [key, setKey] = useState("");
  const [rows, setRows] = useState<Row[]>(() => names.map((name) => ({ include: true, name })));

  const preview = useStemPreview(
    useMemo(() => files.map((f) => f.file), [files]),
    useMemo(() => rows.map((r) => r.include), [rows]),
  );
  const included = rows.flatMap((r, i) => (r.include ? [{ ...files[i]!, name: r.name.trim() || names[i]! }] : []));
  const bpmValue = Number(bpm);
  const bpmOk = bpm.trim() !== "" && Number.isFinite(bpmValue) && bpmValue >= 20 && bpmValue <= 999;
  const sizeProblem = limitProblem(included.map((f) => ({ name: f.name, uploadBytes: f.probe.uploadBytes })));
  const ready = title.trim() !== "" && bpmOk && sizeProblem === null;

  // Leaving mid-upload loses it: the files live in this tab, nowhere else.
  const working = uploading && upload.phase.kind !== "done" && upload.phase.kind !== "idle";
  const guard = useUnsavedGuard(working, "Your upload will stop if you leave this page.");
  useEffect(() => {
    onWorking?.(working);
    return () => onWorking?.(false);
  }, [working, onWorking]);

  const submit = () =>
    gate.requireSignIn("post_seed", "Sign in to plant your seed", () => {
      // Where the tempo came from, and whether the guess was right: the
      // evidence for (or against) building better detection.
      const changed = start.bpm !== null && start.bpm !== bpmValue;
      track("web_upload_bpm", { detail: `${start.source}${changed ? "|changed" : ""}`, value: bpmValue });
      preview.stop();
      onStarted();
      void upload.start({
        stems: included.map((f) => ({ file: f.file, name: f.name, probe: f.probe })),
        title: title.trim(),
        bpm: bpmValue,
        key: key.trim(),
        parentSeedId: parent?.id ?? null,
      });
    });

  if (uploading) {
    return (
      <>
        <UploadProgress
          upload={upload}
          trackNames={included.map((f) => f.name)}
          onRestart={onDone}
          onEdit={onEdit}
          forEvent={forEvent}
          thenJam={thenJam}
        />
        {guard.dialog}
      </>
    );
  }

  return (
    <div className="space-y-4 rounded-2xl border border-hairline bg-raised p-4">
      {chosen === "clips" && (
        <p className="text-xs text-muted-foreground">Each file becomes a track starting at bar 1.</p>
      )}
      {/* A phone: the title on a line of its own, tempo and key under it. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-[1fr_6rem_6rem]">
        <Field label="Title" htmlFor="up-title" className="col-span-2 sm:col-span-1">
          <input
            id="up-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Name your seed"
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
          />
        </Field>
        <Field
          label="BPM"
          htmlFor="up-bpm"
          aside={
            start.source === "audio" && start.bpm !== null && [start.bpm, start.bpm / 2, start.bpm * 2].map(String).includes(bpm) && (
              <BpmHint bpm={bpmValue} onPick={(b) => setBpm(String(b))} />
            )
          }
        >
          <input
            id="up-bpm"
            inputMode="decimal"
            value={bpm}
            onChange={(e) => setBpm(e.target.value)}
            placeholder="120"
            className={cn(
              "w-full rounded-lg border bg-background px-3 py-2 text-sm",
              bpm !== "" && !bpmOk ? "border-removed" : "border-input",
            )}
          />
        </Field>
        <Field label="Key (optional)" htmlFor="up-key">
          <input
            id="up-key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Am"
            maxLength={12}
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm"
          />
        </Field>
      </div>

      {/* Hear it before it goes up: the checked tracks together (a checkbox
          mutes its track as it plays), or one track on its own. */}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={preview.mode === "mix" ? preview.stop : preview.playMix}
          disabled={!rows.some((r) => r.include)}
        >
          {preview.mode === "mix" ? <Square className="size-3.5 fill-current" /> : <Play className="size-3.5 fill-current" />}
          {preview.mode === "mix" ? "Stop" : "Play selected tracks"}
        </Button>
        {preview.loading && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
        {preview.failed && <span className="text-xs text-removed">Couldn't play these files here.</span>}
        {/* Up here, not under a long track list: plant without scrolling. */}
        <Button className="w-full px-6 sm:ml-auto sm:w-auto" disabled={!ready} onClick={submit}>
          {thenJam ? "Plant it & start the jam" : parent ? "Plant as a remix" : "Plant seed"}
        </Button>
      </div>

      <ul className="divide-y divide-border rounded-xl border border-hairline">
        {rows.map((row, i) => (
          <li key={files[i]!.file.path} className="flex items-center gap-3 px-3 py-2.5">
            <input
              type="checkbox"
              checked={row.include}
              aria-label={`Include ${row.name}`}
              onChange={(e) => setRows((all) => all.map((r, j) => (j === i ? { ...r, include: e.target.checked } : r)))}
            />
            <button
              type="button"
              onClick={() => (preview.mode === i ? preview.stop() : preview.playOne(i))}
              aria-label={preview.mode === i ? `Stop ${row.name}` : `Preview ${row.name}`}
              title={preview.mode === i ? "Stop" : "Preview this track"}
              className={cn(
                "flex size-9 shrink-0 items-center justify-center rounded-full border transition-colors",
                preview.mode === i
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-white/15 bg-white/5 text-foreground hover:border-primary/60 hover:text-primary",
              )}
            >
              {preview.mode === i ? <Square className="size-3.5 fill-current" /> : <Play className="ml-0.5 size-3.5 fill-current" />}
            </button>
            <div className="min-w-0 flex-1">
              <input
                value={row.name}
                onChange={(e) => setRows((all) => all.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)))}
                className={cn(
                  "w-full rounded-md bg-transparent px-1 py-0.5 text-sm hover:bg-white/5 focus:bg-background",
                  !row.include && "text-muted-foreground line-through",
                )}
              />
              <TrackWave wave={waves[i] ?? null} dim={!row.include} />
            </div>
            <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{formatDuration(files[i]!.probe.durationSec)}</span>
          </li>
        ))}
      </ul>

      {sizeProblem && <Problem text={sizeProblem} />}

      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={onBack}>Back</Button>
        <p className="ml-auto text-right text-xs text-muted-foreground">
          Seeds are public: anyone can listen to and remix what you plant.
        </p>
      </div>
    </div>
  );
}

/** The most a quiet track's waveform is lifted: enough to read its shape,
 * too little to turn an empty export's noise floor into a waveform. */
const MAX_WAVE_GAIN = 4;

/**
 * A track's waveform, lifted at most MAX_WAVE_GAIN×, so a blank export reads
 * as a flat line while a quiet part still shows its shape.
 */
function TrackWave({ wave, dim }: { wave: Float32Array | null; dim: boolean }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const peak = useMemo(() => (wave ? wave.reduce((m, v) => Math.max(m, Math.abs(v)), 0) : null), [wave]);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !wave) return;
    const draw = () => {
      const g = prepareCanvas(canvas, canvas.clientWidth, canvas.clientHeight);
      // The canvas's own text colour (the theme's), so the wave matches the page.
      const gain = peak ? Math.min(MAX_WAVE_GAIN, 0.95 / peak) : 1;
      if (g) drawEnvelope(g, wave.map((v) => v * gain), canvas.clientWidth, canvas.clientHeight, getComputedStyle(canvas).color);
    };
    draw();
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [wave, dim, peak]);
  if (!wave) return null;
  return (
    <div className="mt-1 h-7 px-1">
      <canvas ref={ref} className={cn("h-full w-full", dim ? "text-white/20" : "text-primary/80")} aria-hidden />
    </div>
  );
}

/**
 * An audio guess can be an octave out (drum & bass heard at half speed, trap
 * at double), so it comes with the two fixes beside it.
 */
function BpmHint({ bpm, onPick }: { bpm: number; onPick: (bpm: number) => void }) {
  const half = Math.round(bpm / 2);
  const double = Math.round(bpm * 2);
  return (
    <div className="-mr-1 flex items-center text-[11px] text-muted-foreground">
      {half >= 20 && (
        <button type="button" onClick={() => onPick(half)} title={`Half tempo: ${half}`} className="rounded px-1 hover:bg-white/10 hover:text-foreground">
          ½×
        </button>
      )}
      {double <= 999 && (
        <button type="button" onClick={() => onPick(double)} title={`Double tempo: ${double}`} className="rounded px-1 hover:bg-white/10 hover:text-foreground">
          2×
        </button>
      )}
    </div>
  );
}

function Field({
  label,
  htmlFor,
  aside,
  className,
  children,
}: {
  label: string;
  htmlFor: string;
  className?: string;
  /** Beside the label, on its line: so it never pushes the inputs out of line. */
  aside?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("min-w-0 space-y-1.5", className)}>
      <div className="flex h-4 items-center justify-between gap-1">
        <label className="text-xs text-muted-foreground" htmlFor={htmlFor}>{label}</label>
        {aside}
      </div>
      {children}
    </div>
  );
}

const STAGE_TEXT: Record<string, string> = {
  converting: "Getting your files ready",
  mixing: "Mixing a preview of the whole track",
  encoding: "Making quick-play previews",
  opening: "Starting the upload",
};

function UploadProgress({
  upload,
  trackNames: names,
  onRestart,
  onEdit,
  forEvent,
  thenJam = false,
}: {
  upload: ReturnType<typeof useStemsUpload>;
  trackNames: string[];
  onRestart: () => void;
  /** Back to the filled-in form, files kept. */
  onEdit: () => void;
  forEvent: ForEvent | null;
  thenJam?: boolean;
}) {
  const { phase, jobs, session } = upload;
  const navigate = useNavigate();
  const jam = useStartJam();

  // Plant it & Start Jam: the room opens on the new seed once it's live.
  const jamOnce = useRef(false);
  const liveSeedId = phase.kind === "done" ? phase.seedId : null;
  const { start: startJam } = jam;
  useEffect(() => {
    if (!thenJam || !liveSeedId || jamOnce.current) return;
    jamOnce.current = true;
    startJam(liveSeedId);
  }, [thenJam, liveSeedId, startJam]);

  // For an event: into the pool the moment it's live, then back to the event.
  const doneSeedId = phase.kind === "done" ? phase.seedId : null;
  const eventSlug = forEvent?.slug ?? null;
  const eventThen = forEvent?.then ?? undefined;
  const pooledOnce = useRef(false);
  useEffect(() => {
    if (!eventSlug || !doneSeedId || pooledOnce.current) return;
    pooledOnce.current = true;
    const back = (state: EventReturnState) => navigate(eventPath(eventSlug), { state });
    webApi(`/events/${encodeURIComponent(eventSlug)}/pool`, json("POST", { versionId: doneSeedId }))
      .then(() => back({ joinStep: eventThen }))
      .catch((e: unknown) =>
        back({ joinStep: eventThen, poolProblem: `Your seed is live, but it didn't go in the pool: ${e instanceof Error ? e.message : "try adding it again"}.` }),
      );
  }, [eventSlug, eventThen, doneSeedId, navigate]);

  // When the current stretch began, and how much was already sent then — a
  // retry's rate should count only what this burst has moved.
  const since = useRef({ kind: phase.kind, at: performance.now(), sentAtStart: 0 });
  const sentNow = jobs.reduce((n, j) => n + j.sent, 0);
  if (since.current.kind !== phase.kind) since.current = { kind: phase.kind, at: performance.now(), sentAtStart: sentNow };
  // Re-render twice a second so the estimate and the check's creep move.
  const [, tick] = useState(0);
  useEffect(() => {
    if (phase.kind !== "sending" && phase.kind !== "finishing") return;
    const t = setInterval(() => tick((n) => n + 1), 500);
    return () => clearInterval(t);
  }, [phase.kind]);
  const totalBytes = jobs.reduce((n, j) => n + j.bytes, 0);
  const elapsedSec = (performance.now() - since.current.at) / 1000;
  const stage: ProgressStage | null =
    phase.kind === "preparing" ? { kind: "preparing", fraction: prepareFraction(phase.stage, phase.done, phase.total) }
    : phase.kind === "sending" ? { kind: "sending", sentBytes: sentNow - since.current.sentAtStart, totalBytes: totalBytes - since.current.sentAtStart, elapsedSec }
    : phase.kind === "finishing" ? { kind: "checking", totalBytes, elapsedSec }
    : null;
  const overall = stage ? overallProgress(phase.kind === "sending" ? { kind: "sending", sentBytes: sentNow, totalBytes, elapsedSec } : stage) : 0;
  const left = stage ? timeLeftText(secondsLeft(stage)) : null;
  const overallBar = stage && (
    <div className="space-y-1.5">
      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
        <div className="h-full rounded-full bg-primary transition-all duration-500 ease-out" style={{ width: `${Math.max(3, Math.round(overall * 100))}%` }} />
      </div>
      <p className="flex justify-between text-xs text-muted-foreground">
        <span>Keep this tab open until it's done.</span>
        {left && <span>{left}</span>}
      </p>
    </div>
  );

  if (phase.kind === "done" && forEvent) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-hairline bg-raised p-6 text-center">
        <CheckCircle className="size-8 text-primary" />
        <p className="text-base font-semibold">Your seed is live</p>
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Putting it in the pool…
        </p>
      </div>
    );
  }

  if (phase.kind === "done" && thenJam && !jam.error) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-hairline bg-raised p-6 text-center">
        <CheckCircle className="size-8 text-primary" />
        <p className="text-base font-semibold">Your seed is live</p>
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Opening the jam room…
        </p>
      </div>
    );
  }

  if (phase.kind === "done") {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-hairline bg-raised p-6 text-center">
        <CheckCircle className="size-8 text-primary" />
        <p className="text-base font-semibold">Your seed is live</p>
        {jam.error && <Problem text={jam.error} />}
        <div className="flex flex-wrap justify-center gap-2">
          <Button asChild>
            <Link to={seedPath(phase.seedId)}>Open Seed</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to={localRoomPath(phase.seedId)}>Edit in DAW</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (phase.kind === "preparing" || (phase.kind === "failed" && !session) || phase.kind === "idle") {
    return (
      <div className="space-y-3 rounded-2xl border border-hairline bg-raised p-4">
        {overallBar}
        {phase.kind === "preparing" && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> {STAGE_TEXT[phase.stage]}
            {phase.total !== undefined && phase.stage === "converting" ? ` (${(phase.done ?? 0) + 1} of ${phase.total})` : "…"}
          </p>
        )}
        {phase.kind === "failed" && (
          <>
            <Problem text={phase.message} />
            {/* Habib read a failed upload as a vanished one: say the files are kept. */}
            <p className="text-sm text-muted-foreground">Your files are still here. Nothing was posted.</p>
          </>
        )}
        <div className="flex justify-center gap-2">
          {phase.kind === "failed" && canRetry(phase.reason) ? (
            <>
              <Button size="sm" onClick={() => void upload.restart()}>
                <RotateCcw className="mr-1 size-3.5" /> Try again
              </Button>
              <Button variant="ghost" size="sm" onClick={onEdit}>Edit details</Button>
            </>
          ) : phase.kind === "failed" ? (
            <Button size="sm" onClick={onEdit}>Back to your files</Button>
          ) : phase.kind === "preparing" ? (
            <Button variant="ghost" size="sm" onClick={() => void upload.cancel().then(onRestart)}>
              <X className="mr-1 size-3.5" /> Cancel
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={onRestart}>Start over</Button>
          )}
        </div>
      </div>
    );
  }

  // One row per track, plus one for the mix and previews Seedbed adds.
  const jobOfTrack = (i: number): JobView | null => {
    const hash = session?.prepared.placements.find((p) => p.trackIndex === i)?.hash;
    return jobs.find((j) => j.kind === "clip" && j.hash === hash) ?? null;
  };
  const extras = jobs.filter((j) => j.kind !== "clip");
  const anyFailed = jobs.some((j) => j.status === "failed");
  const anyActive = jobs.some((j) => j.status === "sending" || j.status === "queued");

  return (
    <div className="space-y-3 rounded-2xl border border-hairline bg-raised p-4">
      {overallBar}
      <ul className="space-y-2">
        {names.map((name, i) => (
          <ProgressRow key={i} name={name} jobs={jobOfTrack(i) ? [jobOfTrack(i)!] : []} onRetry={(ids) => void upload.retry(ids)} />
        ))}
        {extras.length > 0 && (
          <ProgressRow name="Mix and previews" jobs={extras} onRetry={(ids) => void upload.retry(ids)} />
        )}
      </ul>
      {phase.kind === "finishing" && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Seedbed is checking every file arrived intact…
        </p>
      )}
      {phase.kind === "failed" && <Problem text={phase.message} />}
      {anyFailed && !anyActive && (
        <p className="text-sm text-removed">Some files didn't make it. Retry them; the rest are already up.</p>
      )}
      <div className="flex justify-center gap-2">
        {anyFailed && !anyActive && (
          <Button size="sm" onClick={() => void upload.retry()}>
            <RotateCcw className="mr-1 size-3.5" /> Retry failed
          </Button>
        )}
        {phase.kind === "failed" && !anyFailed && (
          <Button size="sm" onClick={() => void upload.retryFinish()}>
            <RotateCcw className="mr-1 size-3.5" /> Try again
          </Button>
        )}
        {phase.kind !== "finishing" && (
          <Button variant="ghost" size="sm" onClick={() => void upload.cancel().then(onRestart)}>
            <X className="mr-1 size-3.5" /> Cancel
          </Button>
        )}
      </div>
    </div>
  );
}

function ProgressRow({ name, jobs, onRetry }: { name: string; jobs: JobView[]; onRetry: (ids: string[]) => void }) {
  // No job: the pool already had these exact bytes, nothing to send.
  const total = jobs.reduce((n, j) => n + j.bytes, 0);
  const sent = jobs.reduce((n, j) => n + j.sent, 0);
  const failed = jobs.filter((j) => j.status === "failed");
  const done = jobs.every((j) => j.status === "sent");
  const percent = total > 0 ? Math.round((sent / total) * 100) : 100;
  return (
    <li className="flex items-center gap-3 text-sm">
      <span className="w-40 min-w-0 truncate">{name}</span>
      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
        <span
          className={cn("block h-full rounded-full transition-all", failed.length > 0 ? "bg-removed" : "bg-primary")}
          style={{ width: `${Math.max(2, percent)}%` }}
        />
      </span>
      <span className="w-16 shrink-0 text-right text-xs text-muted-foreground">
        {failed.length > 0 ? (
          <button type="button" className="text-removed underline" onClick={() => onRetry(failed.map((j) => j.id))}>
            Retry
          </button>
        ) : done ? (
          jobs.length === 0 ? "Already up" : "Done"
        ) : (
          `${percent}%`
        )}
      </span>
    </li>
  );
}
